import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Autocomplete,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import NotificationSnackbar from "./NotificationBar";
const emptyForm = {
  name: "",
  employeeGroupId: "",
};

function EditPayrollGroup({
  open,
  onClose,
  onSave,
  onSaveSuccess,
  payrollGroup,
}) {
  const theme = useTheme();
  const [form, setForm] = useState(emptyForm);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [loadingEmployeeGroups, setLoadingEmployeeGroups] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const fetchEmployeeGroups = async () => {
      try {
        setLoadingEmployeeGroups(true);
        const response = await axios.get("/api/support/employee-groups");
        const data = response.data;
        const list = Array.isArray(data)
          ? data
          : data?.recordset || data?.result || [];

        setEmployeeGroups(list);
      } catch (error) {
        console.error("Error fetching employee groups:", error);

        setSnackbarMessage("Failed to load employee groups");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingEmployeeGroups(false);
      }
    };

    fetchEmployeeGroups();
  }, [open]);

  useEffect(() => {
    if (payrollGroup && open) {
      setForm({
        name:
          payrollGroup.name ??
          payrollGroup.PayrollGroup ??
          payrollGroup.payrollGroup ??
          "",

        employeeGroupId:
          payrollGroup.employeeGroupId ??
          payrollGroup.employee_group_id ??
          payrollGroup.EmployeeGroupId ??
          "",
      });

      setErrors({});
      setSaved(false);
    }
  }, [payrollGroup, open]);
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleEmployeeGroupChange = (event, selectedEmployeeGroup) => {
    const employeeGroupId = selectedEmployeeGroup
      ? selectedEmployeeGroup.id
      : "";

    setForm((prev) => ({
      ...prev,
      employeeGroupId,
    }));

    setErrors((prev) => ({
      ...prev,
      employeeGroupId: "",
    }));
  };

  const selectedEmployeeGroup =
    employeeGroups.find(
      (group) => String(group.id) === String(form.employeeGroupId),
    ) || null;

  const validate = () => {
    const errs = {};

    if (!form.name.trim()) {
      errs.name = "Payroll Group name is required";
    }

    if (!form.employeeGroupId) {
      errs.employeeGroupId = "Employee Group is required";
    }

    setErrors(errs);

    return Object.keys(errs).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;

    if (!payrollGroup?.id) {
      setSnackbarMessage("Payroll Group ID is missing");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        name: form.name.trim(),
        employeeGroupId: form.employeeGroupId,
      };

      const response = await axios.put(
        `/api/support/update-payroll-group/${payrollGroup.id}`,
        updateData,
      );

      setSnackbarMessage(
        response.data?.message || "Payroll Group updated successfully",
      );

      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSaved(true);

      // Pass updated data to parent if needed
      onSave?.(updateData);

      // Close after showing success state
      setTimeout(() => {
        setSaved(false);

        onSaveSuccess?.();

        onClose();
      }, 1000);
    } catch (err) {
      console.error("Payroll Group update error:", err);

      const errorMessage =
        err.response?.data?.message || err.message || "Something went wrong";

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;

    setErrors({});
    setForm(emptyForm);
    setSaved(false);

    onClose();
  };

  // ============================================================
  // FIELD STYLING
  // ============================================================
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),

      "& fieldset": {
        borderColor: alpha(theme.palette.primary.light, 0.5),
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  // ============================================================
  // READ ONLY FIELD STYLING
  // ============================================================
  const readOnlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.action.disabled, 0.05),

      "& fieldset": {
        borderColor: alpha(theme.palette.primary.light, 0.3),
      },
    },

    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      fontWeight: 500,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "visible",
          border: `1px solid ${alpha(theme.palette.primary.light, 0.5)}`,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.primary.light,
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon
                sx={{
                  fontSize: 16,
                  color: "#fff",
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: "#fff",
                  lineHeight: 1.3,
                }}
              >
                Edit Payroll Group
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.7)",
                  mt: 0.3,
                }}
              >
                Update payroll group details
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={{
              color: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(255,255,255,0.3)",

              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* CURRENT VALUE */}
        {payrollGroup?.id && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: "20px",
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#fff",
              }}
            />

            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.65)",
              }}
            >
              ID:
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: "#fff",
              }}
            >
              {payrollGroup.id}
            </Typography>

            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.3)",
              }}
            >
              ·
            </Typography>

            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {payrollGroup.name ||
                payrollGroup.PayrollGroup ||
                payrollGroup.payrollGroup}
            </Typography>
          </Box>
        )}
      </Box>
      <DialogContent
        sx={{
          pt: 2.5,
          pb: 1,
          bgcolor: "#fff",
          overflow: "visible",
        }}
      >
        <Box display="flex" flexDirection="column" gap={1.5}>
          {/* ID */}
          <TextField
            label="ID"
            value={payrollGroup?.id || ""}
            size="small"
            fullWidth
            disabled
            sx={readOnlyFieldSx}
          />

          {/* PAYROLL GROUP NAME */}
          <TextField
            label="Payroll Group Name"
            name="name"
            size="small"
            fullWidth
            value={form.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            disabled={saving}
            sx={fieldSx}
          />

          {/* EMPLOYEE GROUP */}
          <Autocomplete
            options={employeeGroups}
            value={selectedEmployeeGroup}
            onChange={handleEmployeeGroupChange}
            loading={loadingEmployeeGroups}
            disabled={saving}
            getOptionLabel={(option) =>
              option.employeeGroup || option.name || ""
            }
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            noOptionsText="No employee groups found"
            clearOnEscape
            disablePortal
            slotProps={{
              popper: {
                placement: "bottom-start",
                modifiers: [
                  {
                    name: "flip",
                    enabled: false,
                  },
                ],
              },
              paper: {
                sx: {
                  maxHeight: 300,
                },
              },
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start !important",
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {option.employeeGroup || option.name}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                label="Employee Group"
                placeholder="Search employee group..."
                error={!!errors.employeeGroupId}
                helperText={errors.employeeGroupId}
                sx={fieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEmployeeGroups && (
                        <CircularProgress color="inherit" size={18} />
                      )}

                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>

      {/* ======================================================
          FOOTER LINE
      ======================================================= */}
      <Box
        sx={{
          height: "1px",
          bgcolor: alpha(theme.palette.primary.light, 0.4),
        }}
      />

      {/* ======================================================
          FOOTER
      ======================================================= */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          bgcolor: alpha(theme.palette.primary.light, 0.08),
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          disabled={saving}
          sx={{
            textTransform: "none",
            borderColor: alpha(theme.palette.primary.light, 0.5),
            color: "text.secondary",

            "&:hover": {
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={
            saving || saved || !form.name.trim() || !form.employeeGroupId
          }
          startIcon={
            saved ? (
              <CheckIcon sx={{ fontSize: 14 }} />
            ) : saving ? (
              <CircularProgress size={13} color="inherit" />
            ) : null
          }
          sx={{
            textTransform: "none",
            minWidth: 115,

            bgcolor: saved
              ? theme.palette.primary.main
              : theme.palette.primary.light,

            "&:hover": {
              bgcolor: theme.palette.primary.main,
            },

            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.primary.light, 0.4),
            },
          }}
        >
          {saved ? "Saved!" : saving ? "Saving..." : "Save changes"}
        </Button>
      </DialogActions>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default EditPayrollGroup;
